/**
 * Re-baseline a control chart after a sustained shift: the analysis estimates new limits from the chosen window
 * and makes them the chart's limits (a new version; the current ones stay in the chart's history). It opens
 * prefilled from where the run on one side began, for 30 days or up to now; the reason is kept with the new
 * limits. A window too small for a baseline is refused with how many points it needs and how many it holds.
 *
 * Rendered into document.body: a dashboard tile is positioned with a transform and clips what overflows it,
 * which would trap a fixed overlay inside the tile.
 */
import { useEffect, useId, useState, type ChangeEvent } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { useOptionalToast } from "@/lib/ui/toast";
import { useSpcRebaseline } from "@/lib/api/spc";
import {
  fromLocalInput,
  rebaselineErrorText,
  rebaselineProblem,
  toLocalInput,
  type RebaselineDraft,
} from "./episodes";

const FIELD = "flex flex-col gap-1 text-xs font-medium text-on-surface-muted";

export function SpcRebaselineDialog({
  chartId,
  draft,
  onClose,
}: {
  chartId: string;
  draft: RebaselineDraft;
  onClose: () => void;
}) {
  const toast = useOptionalToast();
  const rebaseline = useSpcRebaseline(chartId);
  const [form, setForm] = useState(draft);
  const [problem, setProblem] = useState<string | null>(null);
  const id = useId();
  const busy = rebaseline.isPending;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const setTime = (key: "from" | "to") => (e: ChangeEvent<HTMLInputElement>) => {
    const iso = fromLocalInput(e.target.value);
    if (iso) setForm((f) => ({ ...f, [key]: iso }));
  };

  const submit = () => {
    const invalid = rebaselineProblem(form);
    setProblem(invalid);
    if (invalid) return;
    rebaseline.mutate(
      { from: form.from, to: form.to, reason: form.reason.trim() },
      {
        onSuccess: (res) => {
          toast.push("ok", `New control limits in force (v${res.limits.version}).`);
          onClose();
        },
        onError: (err) => setProblem(rebaselineErrorText(err)),
      },
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        disabled={busy}
        className="absolute inset-0 bg-on-surface/40 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        className="relative flex w-full max-w-md flex-col gap-3 rounded-lg border border-border bg-surface-elevated p-5 text-on-surface shadow-2xl"
      >
        <h2 id={`${id}-title`} className="text-base font-semibold leading-tight">
          Re-baseline this chart
        </h2>
        <p className="text-sm leading-relaxed text-on-surface-muted">
          The analysis estimates new control limits from the readings in this window and puts them in force as a
          new version. Points after the window are checked again against them, as history that never alarms. The
          current limits stay in the chart&apos;s history.
        </p>
        <div className={FIELD}>
          <label htmlFor={`${id}-from`}>From</label>
          <Input
            id={`${id}-from`}
            type="datetime-local"
            value={toLocalInput(form.from)}
            onChange={setTime("from")}
          />
        </div>
        <div className={FIELD}>
          <label htmlFor={`${id}-to`}>To</label>
          <Input id={`${id}-to`} type="datetime-local" value={toLocalInput(form.to)} onChange={setTime("to")} />
        </div>
        <div className={FIELD}>
          <label htmlFor={`${id}-reason`}>Reason (kept with the new limits)</label>
          <Input
            id={`${id}-reason`}
            value={form.reason}
            required
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
          />
        </div>
        {problem && (
          <p role="alert" className="text-sm text-danger">
            {problem}
          </p>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={busy || !form.reason.trim()}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            Re-baseline
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
